import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';

export interface ITowOnEntityAttributes extends IModelAttributes {
	legacyId: number;

	towOnDetails: Array<Models.TowOnDetailEntity | Models.ITowOnDetailEntityAttributes>;
	selectedLengthId: string;
	selectedLength: Models.MeasurementEntity | Models.IMeasurementEntityAttributes;
	selectedWeightId?: string;
	selectedWeight?: Models.MeasurementEntity | Models.IMeasurementEntityAttributes;
	selectedWidthId?: string;
	selectedWidth?: Models.WidthEntity | Models.IWidthEntityAttributes;
	exactWeight?: number | null;
	exactWeightUnit?: string | null;
	towOnTypeId?: string;
	towOnType?: Models.TowOnTypeEntity | Models.ITowOnTypeEntityAttributes;
}
@entity('TowOnEntity', 'Tow On')
export default class TowOnEntity extends Model implements ITowOnEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<TowOnEntity, number>()
	public legacyId: number;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public towOnDetails: Models.TowOnDetailEntity[] = [];

	@observable
	@attribute()
	public selectedLengthId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public selectedLength: Models.MeasurementEntity;

	@observable
	@attribute()
	public selectedWeightId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public selectedWeight: Models.MeasurementEntity;

	@observable
	@attribute()
	public selectedWidthId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public selectedWidth: Models.WidthEntity;

	@observable
	@attribute<TowOnEntity, number | null>()
	public exactWeight: number | null;

	@observable
	@attribute<TowOnEntity, string | null>()
	public exactWeightUnit: string | null;

	@observable
	@attribute()
	public towOnTypeId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public towOnType: Models.TowOnTypeEntity;

	constructor(attributes?: Partial<ITowOnEntityAttributes>) {
		super(attributes);

		this.towOnDetails = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<ITowOnEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.legacyId !== undefined) {
				this.legacyId = attributes.legacyId;
			}
			if (attributes.towOnDetails !== undefined && Array.isArray(attributes.towOnDetails)) {
				if (!this.towOnDetails) {
					this.towOnDetails = [];
				}
				for (const model of attributes.towOnDetails) {
					if (model instanceof Models.TowOnDetailEntity) {
						this.towOnDetails.push(model);
					} else {
						this.towOnDetails.push(new Models.TowOnDetailEntity(model));
					}
				}
			}
			if (attributes.selectedLengthId !== undefined) {
				this.selectedLengthId = attributes.selectedLengthId;
			}
			if (attributes.selectedLength !== undefined) {
				if (attributes.selectedLength === null) {
					this.selectedLength = attributes.selectedLength;
				} else if (attributes.selectedLength instanceof Models.MeasurementEntity) {
					this.selectedLength = attributes.selectedLength;
					this.selectedLengthId = attributes.selectedLength.id;
				} else {
					this.selectedLength = new Models.MeasurementEntity(attributes.selectedLength);
					this.selectedLengthId = this.selectedLength.id;
				}
			}
			if (attributes.selectedWeightId !== undefined) {
				this.selectedWeightId = attributes.selectedWeightId;
			}
			if (attributes.selectedWeight !== undefined) {
				if (attributes.selectedWeight === null) {
					this.selectedWeight = attributes.selectedWeight;
				} else if (attributes.selectedWeight instanceof Models.MeasurementEntity) {
					this.selectedWeight = attributes.selectedWeight;
					this.selectedWeightId = attributes.selectedWeight.id;
				} else {
					this.selectedWeight = new Models.MeasurementEntity(attributes.selectedWeight);
					this.selectedWeightId = this.selectedWeight.id;
				}
			}
			if (attributes.selectedWidthId !== undefined) {
				this.selectedWidthId = attributes.selectedWidthId;
			}
			if (attributes.selectedWidth !== undefined) {
				if (attributes.selectedWidth === null) {
					this.selectedWidth = attributes.selectedWidth;
				} else if (attributes.selectedWidth instanceof Models.WidthEntity) {
					this.selectedWidth = attributes.selectedWidth;
					this.selectedWidthId = attributes.selectedWidth.id;
				} else {
					this.selectedWidth = new Models.WidthEntity(attributes.selectedWidth);
					this.selectedWidthId = this.selectedWidth.id;
				}
			}
			if (attributes.exactWeight !== undefined) {
				this.exactWeight = attributes.exactWeight;
			}
			if (attributes.exactWeightUnit !== undefined) {
				this.exactWeightUnit = attributes.exactWeightUnit;
			}
			if (attributes.towOnTypeId !== undefined) {
				this.towOnTypeId = attributes.towOnTypeId;
			}
			if (attributes.towOnType !== undefined) {
				if (attributes.towOnType === null) {
					this.towOnType = attributes.towOnType;
				} else if (attributes.towOnType instanceof Models.TowOnTypeEntity) {
					this.towOnType = attributes.towOnType;
					this.towOnTypeId = attributes.towOnType.id;
				} else {
					this.towOnType = new Models.TowOnTypeEntity(attributes.towOnType);
					this.towOnTypeId = this.towOnType.id;
				}
			}
		}
	}

	public errors: { [key in 'selectedLengthId' | 'towOnTypeId']?: string } = {};
}
